import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { LiksIconButton } from '../../../components/liks/LiksIconButton/LiksIconButton';
import { LiksTooltip } from '../../../components/liks/LiksTooltip/LiksTooltip';
import { useI18n } from '../../../i18n/useI18n';
import { resolveBackendBaseUrl } from '../../../utils/backendBaseUrl';
import './ChatbotEmbedSnippetCard.css';

type ChatbotEmbedSnippetCardProps = {
  chatbotId: string;
};

export function ChatbotEmbedSnippetCard({
  chatbotId,
}: ChatbotEmbedSnippetCardProps) {
  const { translate } = useI18n();
  const [copied, setCopied] = useState(false);
  const backendBase = resolveBackendBaseUrl();
  const scriptUrl = `${backendBase}/embed.js`;

  const snippet = `<!-- LIKS AI Chatbot Widget -->
<div id="liks-chatbot-${chatbotId}"></div>
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${scriptUrl}';
    script.setAttribute('data-chatbot-id', '${chatbotId}');
    script.async = true;
    document.head.appendChild(script);
  })();
</script>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="chatbot-settings__card">
      <div className="chatbot-settings__body">
        <h2 className="chatbot-settings__title">
          {translate('chatbots.settings.embedSnippet.title')}
        </h2>
        <p className="chatbot-settings__cors-hint">
          {translate('chatbots.settings.embedSnippet.description')}
        </p>
        <div className="chatbot-settings__snippet-window">
          <pre className="chatbot-settings__snippet">{snippet}</pre>
          <LiksTooltip
            className="chatbot-settings__snippet-copy"
            content={
              copied
                ? translate('chatbots.settings.embedSnippet.copied')
                : translate('chatbots.settings.embedSnippet.copy')
            }
            placement="top-end"
          >
            <LiksIconButton
              type="button"
              variant="outlined"
              aria-label={
                copied
                  ? translate('chatbots.settings.embedSnippet.copied')
                  : translate('chatbots.settings.embedSnippet.copy')
              }
              onClick={() => {
                void handleCopy();
              }}
            >
              {copied ? (
                <Check strokeWidth={2} aria-hidden />
              ) : (
                <Copy strokeWidth={2} aria-hidden />
              )}
            </LiksIconButton>
          </LiksTooltip>
        </div>
        <div className="chatbot-settings__embed-steps">
          <h3 className="chatbot-settings__embed-steps-title">
            {translate('chatbots.settings.embedSnippet.stepsTitle')}
          </h3>
          <p className="chatbot-settings__embed-steps-description">
            {translate('chatbots.settings.embedSnippet.stepsDescription')}
          </p>
          <ol className="chatbot-settings__embed-step-list">
            <li className="chatbot-settings__embed-step">
              <span className="chatbot-settings__embed-step-index">1</span>
              <div className="chatbot-settings__embed-step-body">
                <p className="chatbot-settings__embed-step-title">
                  {translate('chatbots.settings.embedSnippet.step1Title')}
                </p>
                <p className="chatbot-settings__embed-step-text">
                  {translate('chatbots.settings.embedSnippet.step1Description')}
                </p>
              </div>
            </li>
            <li className="chatbot-settings__embed-step">
              <span className="chatbot-settings__embed-step-index">2</span>
              <div className="chatbot-settings__embed-step-body">
                <p className="chatbot-settings__embed-step-title">
                  {translate('chatbots.settings.embedSnippet.step2Title')}
                </p>
                <p className="chatbot-settings__embed-step-text">
                  {translate('chatbots.settings.embedSnippet.step2Description')}
                </p>
              </div>
            </li>
            <li className="chatbot-settings__embed-step">
              <span
                className="chatbot-settings__embed-step-index chatbot-settings__embed-step-index--done"
                aria-hidden
              >
                <Check strokeWidth={2.5} />
              </span>
              <div className="chatbot-settings__embed-step-body">
                <p className="chatbot-settings__embed-step-title">
                  {translate('chatbots.settings.embedSnippet.step3Title')}
                </p>
                <p className="chatbot-settings__embed-step-text">
                  {translate('chatbots.settings.embedSnippet.step3Description')}
                </p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
